import { useState } from 'react';
import type { ReactElement } from 'react';
import { paisa, type Money } from '@fleet/shared';
import {
  useAddBookingChargeMutation,
  useBookingChargesQuery,
  useRemoveBookingChargeMutation,
} from '@/api/endpoints';
import { Button } from '@/design/Button';
import { MoneyText } from '@/design/Data';
import { Field, FieldRow, MoneyInput, TextInput } from '@/design/Field';
import { Panel, PanelBody } from '@/design/Panel';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';

/**
 * What got added after the keys went out.
 *
 * A scratch on the bumper, a wash after the Mustang road, a route the customer
 * changed halfway. These are agreed at the counter with the customer standing
 * there, which is why they live on the booking's own screen next to the
 * handover buttons and not on the booking form — nobody knows about a scratch
 * when they are taking the booking.
 *
 * Deliberately free text and a free amount. A price list with "scratch" on it
 * at a fixed rate would be a fiction; what actually happens is a number agreed
 * out loud. The description is what prints on the invoice, so it has to read
 * like a line on a bill rather than like a note to self.
 */

interface Props {
  readonly bookingId: string;
  readonly reference: string;
  /** Charges are frozen once the booking is invoiced, like the rest of the price. */
  readonly locked: boolean;
}

export function BookingChargesPanel({ bookingId, reference, locked }: Props): ReactElement {
  const charges = useBookingChargesQuery(bookingId);
  const [add, adding] = useAddBookingChargeMutation();
  const [remove, removing] = useRemoveBookingChargeMutation();
  const toast = useToast();

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<Money>(paisa(0));

  const problems = readApiError(adding.error ?? removing.error);
  const rows = charges.data?.results ?? [];
  const added = rows.reduce((sum, row) => sum + row.amount, 0);
  const ready = description.trim().length >= 3 && amount > 0;

  async function save(): Promise<void> {
    try {
      await add({
        booking: bookingId,
        description: description.trim(),
        quantity: 1,
        unit_amount: amount,
      }).unwrap();
      setDescription('');
      setAmount(paisa(0));
    } catch {
      // Shown under the field it belongs to.
    }
  }

  async function drop(id: string, what: string): Promise<void> {
    try {
      await remove(id).unwrap();
      toast.done(`“${what}” removed from ${reference}.`);
    } catch {
      toast.problem(readApiError(removing.error).detail ?? 'That charge was not removed.');
    }
  }

  return (
    <Panel
      title="Extra charges"
      note={
        added === 0
          ? 'Damage, cleaning, a longer route — anything agreed after the booking was taken'
          : `${String(rows.length)} added, in the total below`
      }
    >
      {rows.length > 0 && (
        <ul className="divide-y divide-rule">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="min-w-0 flex-1">{row.description}</span>
              <span className="tabular-nums font-medium">
                <MoneyText amount={paisa(row.amount)} />
              </span>
              {!locked && (
                <Button
                  intent="quiet"
                  disabled={removing.isLoading}
                  onClick={() => void drop(row.id, row.description)}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <PanelBody>
        {locked ? (
          <p className="text-small text-ink-muted">
            This booking has been invoiced, so its charges are fixed. Void the invoice to change
            them.
          </p>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <FieldRow>
              <Field label="What for" error={problems.fields['description']}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    value={description}
                    onChange={setDescription}
                  />
                )}
              </Field>
              <Field label="Amount" error={problems.fields['unit_amount']}>
                {({ id, describedBy }) => (
                  <MoneyInput
                    id={id}
                    describedBy={describedBy}
                    value={amount}
                    onChange={setAmount}
                  />
                )}
              </Field>
            </FieldRow>

            {problems.detail !== undefined && (
              <p className="font-medium text-alert" role="alert">
                {problems.detail}
              </p>
            )}

            <div>
              <Button intent="primary" type="submit" disabled={adding.isLoading || !ready}>
                {adding.isLoading ? 'Adding' : 'Add charge'}
              </Button>
            </div>
          </form>
        )}
      </PanelBody>
    </Panel>
  );
}
